import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { NotificationContext } from '../GrowlNotifications';
import { defineMessages, useIntl } from 'react-intl';
import { Field, Form, Formik } from 'formik';
import { Button } from 'primereact/button';
import gql from 'graphql-tag';
import * as Yup from 'yup';
import apollo from '../../services/apollo';
import { growlErrors } from '../../utils/NotificationUtil';
import { Dialog } from 'primereact/dialog';
import { InputNumberComponent } from '../prime-formik';

const CC_INTERESTS = gql`
  mutation($input: ReportWithAssessmentGenericInput!) {
    generateCareerCounselingInterestsReport(input: $input) {
      url
    }
  }
`;
const CC_PERSONALITY = gql`
  mutation($input: ReportWithAssessmentGenericInput!) {
    generateCareerCounselingPersonalityReport(input: $input) {
      url
    }
  }
`;

const messages = defineMessages({
  header: { id: 'report.match-percentage.dialog-header', defaultMessage: 'Choose the minimum match percentage' },
  warning: {
    id: 'report.match-percentage.dialog-warning',
    defaultMessage: 'If the match percentage is too high, you might not get any matches',
  },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  generate: { id: 'actions.generate', defaultMessage: 'Generate' },
  CC_INTERESTS: { id: 'report.CC.interests', defaultMessage: 'Career Counseling - Interests' },
  CC_PERSONALITY: { id: 'report.CC.personality', defaultMessage: 'Career Counseling - Personality' },
});

const DownloadReportButtonWithDialog = ({ type, reportParams }) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();

  const modalHeader = <div className="matched-percentage-centered-content">{formatMessage(messages.header)}</div>;

  const generateReportRequest = async (inputValues, report) => {
    try {
      const result = await apollo.mutate({
        mutation: report,
        variables: {
          input: inputValues,
        },
      });
      switch (report) {
        case CC_INTERESTS: {
          window.open(result.data.generateCareerCounselingInterestsReport.url, '_blank');
          break;
        }
        case CC_PERSONALITY: {
          window.open(result.data.generateCareerCounselingPersonalityReport.url, '_blank');
          break;
        }
        default: {
          throw new Error(`Report type not supported: ${report}`);
        }
      }
    } catch (graphqlResponse) {
      growlErrors(growl, graphqlResponse);
    }
  };

  const generateReportByType = async (inputValues, reportType) => {
    switch (reportType) {
      case 'CC_INTERESTS': {
        return generateReportRequest(inputValues, CC_INTERESTS);
      }
      case 'CC_PERSONALITY': {
        return generateReportRequest(inputValues, CC_PERSONALITY);
      }
      default: {
        throw new Error(`Report type not supported: ${reportType}`);
      }
    }
  };

  const generateReport = async (params) => generateReportByType(params, type);

  const initialValues = {
    minimumMatchPercentage: 0,
  };
  const validationSchema = Yup.object().shape({
    minimumMatchPercentage: Yup.number()
      .min(0)
      .max(100)
      .required(),
  });

  return (
    <div className="field field--centered mt5">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { resetForm }) => {
          const { minimumMatchPercentage } = values;
          const params = {
            ...reportParams,
            minimumMatchPercentage,
          };

          try {
            await generateReport(params);
          } catch (error) {
            growlErrors(growl, error);
          } finally {
            setDialogOpen(false);
            resetForm();
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <Dialog header={modalHeader} visible={dialogOpen} onHide={() => setDialogOpen(false)} modal>
              <div className="panel panel-no-border">
                <div className="matched-percentage-warning">
                  <Button
                    label={formatMessage(messages.warning)}
                    icon="pi pi-info-circle"
                    disabled
                    className="p-button-warning"
                  />
                </div>
                <div className="panel-body match-percentage-form">
                  <div className="match-percentage-input">
                    <Field name="minimumMatchPercentage" component={InputNumberComponent} />
                  </div>
                  <div className="buttons">
                    <Button
                      className="p-button-secondary"
                      label={formatMessage(messages.cancel)}
                      type="button"
                      onClick={() => setDialogOpen(false)}
                    />
                    <Button
                      type="submit"
                      icon={isSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
                      label={formatMessage(messages.generate)}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>
            </Dialog>
            <Button
              type="button"
              label={formatMessage(messages[type.toUpperCase()])}
              onClick={() => setDialogOpen(true)}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

DownloadReportButtonWithDialog.propTypes = {
  type: PropTypes.string.isRequired,
  reportParams: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default DownloadReportButtonWithDialog;
